import axios from "axios";

const ADMIN_API_BASE_URL = "/ofc/api/v1/admin/announcements";
const PUBLIC_API_BASE_URL = "/ofc/api/v1/announcements";

// Configure axios defaults
axios.defaults.withCredentials = true;

export interface AnnouncementScheduleDTO {
  id?: number;
  announcementId?: number;
  sendAt: string | number; // ISO 8601 datetime or Unix timestamp in seconds
  scheduleType: "INITIAL" | "REMINDER";
  status?: "PENDING" | "SENT" | "FAILED";
  errorMessage?: string;
  sentAt?: string | number;
  createdAt?: string | number;
  updatedAt?: string | number;
}

export interface AnnouncementDTO {
  id?: number;
  title: string;
  content: string;
  imageUrl?: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  priority: "NORMAL" | "IMPORTANT";
  category?: string;
  status: "DRAFT" | "PUBLISHED" | "ARCHIVED";
  isPinned?: boolean;
  createdBy?: number;
  createdByName?: string;
  updatedBy?: number;
  updatedByName?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  schedules?: AnnouncementScheduleDTO[];
}

export interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
  error?: string;
}

class AnnouncementsApiService {
  // ==================== Admin Operations ====================

  /**
   * Get all announcements (Admin only)
   */
  async getAllAnnouncements(): Promise<AnnouncementDTO[]> {
    try {
      const response =
        await axios.get<ApiResponse<AnnouncementDTO[]>>(ADMIN_API_BASE_URL);
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || "Failed to fetch announcements");
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to fetch announcements",
        );
      }
      throw error;
    }
  }

  /**
   * Get announcement by ID (Admin only)
   */
  async getAnnouncementById(id: number): Promise<AnnouncementDTO> {
    try {
      const response = await axios.get<ApiResponse<AnnouncementDTO>>(
        `${ADMIN_API_BASE_URL}/${id}`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || "Failed to fetch announcement");
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to fetch announcement",
        );
      }
      throw error;
    }
  }

  /**
   * Create a new announcement (Admin only)
   */
  async createAnnouncement(
    announcement: AnnouncementDTO,
  ): Promise<AnnouncementDTO> {
    try {
      const response = await axios.post<ApiResponse<AnnouncementDTO>>(
        ADMIN_API_BASE_URL,
        announcement,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || "Failed to create announcement");
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to create announcement",
        );
      }
      throw error;
    }
  }

  /**
   * Update an existing announcement (Admin only)
   */
  async updateAnnouncement(
    id: number,
    announcement: AnnouncementDTO,
  ): Promise<AnnouncementDTO> {
    try {
      const response = await axios.put<ApiResponse<AnnouncementDTO>>(
        `${ADMIN_API_BASE_URL}/${id}`,
        announcement,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || "Failed to update announcement");
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to update announcement",
        );
      }
      throw error;
    }
  }

  /**
   * Delete an announcement (Admin only)
   */
  async deleteAnnouncement(id: number): Promise<void> {
    try {
      const response = await axios.delete<ApiResponse<void>>(
        `${ADMIN_API_BASE_URL}/${id}`,
      );
      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to delete announcement");
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to delete announcement",
        );
      }
      throw error;
    }
  }
  /**
   * Publish an announcement (Admin only)
   */
  async publishAnnouncement(id: number): Promise<AnnouncementDTO> {
    try {
      const response = await axios.post<ApiResponse<AnnouncementDTO>>(
        `${ADMIN_API_BASE_URL}/${id}/publish`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to publish announcement",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to publish announcement",
        );
      }
      throw error;
    }
  }

  /**
   * Unpublish an announcement (Admin only)
   */
  async unpublishAnnouncement(id: number): Promise<AnnouncementDTO> {
    try {
      const response = await axios.post<ApiResponse<AnnouncementDTO>>(
        `${ADMIN_API_BASE_URL}/${id}/unpublish`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to unpublish announcement",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to unpublish announcement",
        );
      }
      throw error;
    }
  }

  /**
   * Archive an announcement (Admin only)
   */
  async archiveAnnouncement(id: number): Promise<AnnouncementDTO> {
    try {
      const response = await axios.post<ApiResponse<AnnouncementDTO>>(
        `${ADMIN_API_BASE_URL}/${id}/archive`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to archive announcement",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to archive announcement",
        );
      }
      throw error;
    }
  }

  /**
   * Toggle pin status (Admin only)
   */
  async togglePin(id: number): Promise<AnnouncementDTO> {
    try {
      const response = await axios.post<ApiResponse<AnnouncementDTO>>(
        `${ADMIN_API_BASE_URL}/${id}/toggle-pin`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || "Failed to toggle pin");
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.error || "Failed to toggle pin");
      }
      throw error;
    }
  }

  /**
   * Manually send notification for an announcement (Admin only)
   * Works for any status including DRAFT
   */
  async sendNotificationNow(id: number): Promise<boolean> {
    try {
      const response = await axios.post<ApiResponse<boolean>>(
        `${ADMIN_API_BASE_URL}/${id}/send-notification`,
      );
      if (!response.data.success) {
        throw new Error(response.data.error || "Failed to send notification");
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to send notification",
        );
      }
      throw error;
    }
  }

  /**
   * Search announcements (Admin only)
   */
  async searchAnnouncements(keyword: string): Promise<AnnouncementDTO[]> {
    try {
      const response = await axios.get<ApiResponse<AnnouncementDTO[]>>(
        `${ADMIN_API_BASE_URL}/search`,
        { params: { keyword } },
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to search announcements",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to search announcements",
        );
      }
      throw error;
    }
  }

  /**
   * Filter by status (Admin only)
   */
  async filterByStatus(
    status: "DRAFT" | "PUBLISHED" | "ARCHIVED",
  ): Promise<AnnouncementDTO[]> {
    try {
      const response = await axios.get<ApiResponse<AnnouncementDTO[]>>(
        `${ADMIN_API_BASE_URL}/filter/status/${status}`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to filter announcements",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to filter announcements",
        );
      }
      throw error;
    }
  }

  /**
   * Get all categories (Admin only)
   */
  async getCategories(): Promise<string[]> {
    try {
      const response = await axios.get<ApiResponse<string[]>>(
        `${ADMIN_API_BASE_URL}/categories`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(response.data.error || "Failed to fetch categories");
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to fetch categories",
        );
      }
      throw error;
    }
  }

  // ==================== Public Operations ====================

  /**
   * Get active announcements (Public)
   */
  async getActiveAnnouncements(): Promise<AnnouncementDTO[]> {
    try {
      const response = await axios.get<ApiResponse<AnnouncementDTO[]>>(
        `${PUBLIC_API_BASE_URL}/active`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to fetch active announcements",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to fetch active announcements",
        );
      }
      throw error;
    }
  }

  /**
   * Get pinned announcements (Public)
   */
  async getPinnedAnnouncements(): Promise<AnnouncementDTO[]> {
    try {
      const response = await axios.get<ApiResponse<AnnouncementDTO[]>>(
        `${PUBLIC_API_BASE_URL}/pinned`,
      );
      if (!response.data.success || !response.data.data) {
        throw new Error(
          response.data.error || "Failed to fetch pinned announcements",
        );
      }
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.error || "Failed to fetch pinned announcements",
        );
      }
      throw error;
    }
  }
}

const announcementsApiService = new AnnouncementsApiService();
export default announcementsApiService;
